"use client"

/**
 * An appointment's photos and videos, under its comment (boona docs/codebase/attachments/README.md §1):
 * the attach buttons, the files on their way up, one chip for the saved ones (the owner's "C"), the dark
 * viewer (the owner's "A"). Wraps the comment so a file dropped on it or pasted into it is attached.
 * Files attach at once — no Save needed, like the comment itself.
 */
import * as React from "react"
import { HardDrive } from "lucide-react"
import { useTranslation } from "react-i18next"
import { deleteAttachment, listRecordAttachments, refusal } from "@/lib/services/attachmentsService"
import { cn } from "@/lib/utils"
import { AttachControls, useFileDrop } from "./AttachControls"
import { AttachmentChip } from "./AttachmentChip"
import { AttachmentViewer } from "./AttachmentViewer"
import { PendingUploads } from "./PendingUploads"
import { toItem } from "./mapItem"
import type { AttachmentItem } from "./types"
import { useAttachmentUploader } from "./useAttachmentUploader"

export function RecordAttachments({ recordId, children }: { recordId: number; children?: React.ReactNode }) {
  const { t } = useTranslation()
  const [items, setItems] = React.useState<AttachmentItem[]>([])
  const [viewer, setViewer] = React.useState<number | null>(null)
  const [denied, setDenied] = React.useState(false)
  const [notice, setNotice] = React.useState<string | null>(null)

  const load = React.useCallback(async () => {
    try {
      setItems((await listRecordAttachments(recordId)).map(toItem))
      setDenied(false)
    } catch (err) {
      if (refusal(err).status === 403) setDenied(true)
    }
  }, [recordId])

  React.useEffect(() => { setItems([]); setViewer(null); void load() }, [load])

  // a video converting on the server: look again until it is ready
  const converting = items.some((i) => i.processing)
  React.useEffect(() => {
    if (!converting) return
    const id = window.setInterval(() => { void load() }, 5000)
    return () => window.clearInterval(id)
  }, [converting, load])

  const uploader = useAttachmentUploader({ kind: "record", recordId }, {
    mode: "attach", existing: items.length, onUploaded: (item) => setItems((all) => [...all, item]),
  })
  React.useEffect(() => {
    const quota = uploader.pending.find((u) => u.state === "error" && /1 GB/.test(u.error || ""))
    setNotice(quota ? quota.error! : null)
  }, [uploader.pending])

  const drop = useFileDrop((f) => void uploader.add(f), denied)

  if (denied) return <>{children}</>
  return (
    <div {...drop.props} className={cn("rounded-md", drop.over && "outline-dashed outline-2 outline-offset-4 outline-sky-400")}>
      {children}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <AttachControls onFiles={(f) => void uploader.add(f)} />
        <AttachmentChip items={items} onOpen={(i) => setViewer(i)} />
      </div>
      <PendingUploads items={uploader.pending} onRemove={uploader.remove} />
      {notice && (
        <div className="mt-2 flex items-start gap-2 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
          <HardDrive className="mt-0.5 h-3.5 w-3.5 shrink-0" />{notice}
        </div>
      )}
      <AttachmentViewer
        items={items}
        index={viewer}
        onIndexChange={setViewer}
        onDelete={items[viewer ?? -1]?.canDelete ? async (item) => {
          if (!window.confirm(t("attachments.delete_confirm", "Delete this file? It can't be undone."))) return
          await deleteAttachment(Number(item.id))
          const left = items.filter((i) => i.id !== item.id)
          setItems(left)
          setViewer(left.length ? Math.min(viewer ?? 0, left.length - 1) : null)
        } : undefined}
      />
    </div>
  )
}
